import { TANK_CONFIG } from "../config";
import * as THREE from "three";

export interface BarrelControls {
  barrelUp: boolean;
  barrelDown: boolean;
}

export class BarrelController {
  private barrel: THREE.Object3D;
  private barrelPivot: THREE.Object3D;
  private controls: BarrelControls;
  private lastLogTime: number = 0;
  private readonly LOG_INTERVAL: number = 60000; // Log no more than once per 10 seconds

  private savedRotation: number = 0; //debug

  private currentVelocity: number = 0;
  private readonly ACCELERATION: number = 10; // Increased from 40 to 120 for much more responsive movement
  private readonly DECELERATION: number = 20; // Increased from 60 to 180

  // Rotation limits in radians
  // These limits are for the raw barrelPivot.rotation.x without any offset
  private readonly MIN_ROTATION: number =
    TANK_CONFIG.TURRET.BARREL_MIN_ELEVATION;
  private readonly MAX_ROTATION: number =
    TANK_CONFIG.TURRET.BARREL_MAX_ELEVATION;

  constructor(
    barrel: THREE.Object3D,
    barrelPivot: THREE.Object3D,
    controls: BarrelControls
  ) {
    if (!barrel) {
      throw new Error("BarrelController: barrel parameter is required");
    }
    if (!barrelPivot) {
      throw new Error("BarrelController: barrelPivot parameter is required");
    }
    if (!controls) {
      throw new Error("BarrelController: controls parameter is required");
    }

    this.barrel = barrel;
    this.barrelPivot = barrelPivot;
    this.controls = controls;

    // Log initial state with hierarchy details
    const printHierarchy = (obj: THREE.Object3D, depth: number = 0): string => {
      let result =
        "  ".repeat(depth) + `- ${obj.type} (${obj.name || "unnamed"})\n`;
      obj.children.forEach(
        (child) => (result += printHierarchy(child, depth + 1))
      );
      return result;
    };
  }

  update(deltaTime: number) {
    if (!this.barrelPivot) {
      return;
    }

    // Use the speed from config but multiply it by 1.5 to make movement more noticeable
    const maxSpeed = TANK_CONFIG.TURRET.BARREL_ELEVATION_SPEED;

    // Important: We're not using offsetRotation in our comparisons anymore
    const currentRotation = this.barrelPivot.rotation.x;

    // Check if we've reached rotation limits
    const atMinRotation = currentRotation <= this.MIN_ROTATION;
    const atMaxRotation = currentRotation >= this.MAX_ROTATION;

    let targetVelocity = 0;
    // Barrel Up = negative velocity (rotation decreases) - this moves the barrel upward
    // Barrel Down = positive velocity (rotation increases) - this moves the barrel downward
    if (this.controls.barrelUp && !atMinRotation) {
      targetVelocity = -maxSpeed;
    } else if (this.controls.barrelDown && !atMaxRotation) {
      targetVelocity = maxSpeed;
    } else {
      // nothing
    }

    // Apply acceleration/deceleration
    if (targetVelocity !== 0) {
      const acceleration = this.ACCELERATION * deltaTime;
      if (targetVelocity > this.currentVelocity) {
        this.currentVelocity = Math.min(
          this.currentVelocity + acceleration,
          targetVelocity
        );
      } else {
        this.currentVelocity = Math.max(
          this.currentVelocity - acceleration,
          targetVelocity
        );
      }
    } else {
      const deceleration = this.DECELERATION * deltaTime;
      if (this.currentVelocity > 0) {
        this.currentVelocity = Math.max(0, this.currentVelocity - deceleration);
      } else if (this.currentVelocity < 0) {
        this.currentVelocity = Math.min(0, this.currentVelocity + deceleration);
      }
    }

    if (this.currentVelocity !== 0) {
      // Significantly increase the impact of each frame's rotation by using a larger multiplier
      const rotationDelta = this.currentVelocity * deltaTime * 2.5;
      let newRotation = this.barrelPivot.rotation.x + rotationDelta;

      // In the update method where rotation is applied
      if (Math.abs(newRotation) > Math.abs(this.MAX_ROTATION)) {
        console.warn("[BarrelController] Attempted to rotate beyond limits:", {
          attempted: newRotation,
          max: this.MAX_ROTATION,
        });
        newRotation = Math.sign(newRotation) * this.MAX_ROTATION;
      }

      // Clamp to the raw limits
      const clampedRotation = Math.max(
        this.MIN_ROTATION,
        Math.min(this.MAX_ROTATION, newRotation)
      );

      // Apply rotation to the barrelPivot object
      this.barrelPivot.rotation.x = clampedRotation;
      this.savedRotation = clampedRotation;

      // Force matrix updates to ensure changes propagate
      this.barrelPivot.updateMatrix();
      this.barrelPivot.updateMatrixWorld(true);
      this.barrel.updateMatrix();
      this.barrel.updateMatrixWorld(true);
    }

    // Log state periodically
    const now = Date.now();
    if (now - this.lastLogTime > this.LOG_INTERVAL) {
      const worldPos = this.barrel.getWorldPosition(new THREE.Vector3());
      this.lastLogTime = now;
    }

    if (this.barrelPivot.rotation.x !== this.savedRotation) {
      this.barrelPivot.rotation.x = this.savedRotation;
      this.barrelPivot.updateMatrix();
      this.barrelPivot.updateMatrixWorld(true);
    }
  }

  getCurrentRotation(): number {
    return this.barrelPivot.rotation.x;
  }

  getCurrentVelocity(): number {
    return this.currentVelocity;
  }

  getCurrentControls(): BarrelControls {
    return this.controls;
  }

  setRotation(rotation: number) {
    const clampedRotation = Math.max(
      Math.min(rotation, this.MAX_ROTATION),
      this.MIN_ROTATION
    );
    this.barrelPivot.rotation.x = clampedRotation;
    this.currentVelocity = 0;
  }

  setElevation(elevation: number) {
    this.setRotation(elevation);
  }

  // Public method to update controls
  setControls(newControls: BarrelControls) {
    this.controls = newControls;
  }
}
